import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Col, Dropdown, Input, Menu, Modal, Row } from 'antd';
import { TbCircleFilled, TbSearch, TbTimelineEventPlus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useBillingSnapshot } from '@/hooks/useBillingSnapshot';
import { WorkspaceResourceCollection, workspaceService } from '@/services/database';
import {
    DropdownOptionContent,
    EmptySection,
    LearnMoreFooter,
    VirtualizedList as List,
    SkeletonList,
    SmallTag,
} from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import {
    getAvailablePipelineItemDefinitions,
    isRelationsPipelineItemCollection,
    PIPELINE_ITEM_DEFINITION_BY_COLLECTION,
    PipelineItemResourceCollection,
} from '@/shared/pipelineItems';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { PipelineColumn, SetStateMethod } from '@/types';

import ListItem from './ListItem';

interface AddToColumnModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    pipeline?: any;
    column: PipelineColumn;
    columns: PipelineColumn[];
    setColumns: SetStateMethod<Partial<PipelineColumn>[]>;
}

const AddToColumnModal: FC<AddToColumnModalProps> = ({
    open,
    setOpen,
    pipeline,
    column,
    columns,
    setColumns,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const [option, setOption] = useState<PipelineItemResourceCollection>('investments');

    const [input, setInput] = useState('');

    const [results, setResults] = useState<any[]>([]);

    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { selectedUser: user } = useUserStore();
    const { relationsEnabled } = useBillingSnapshot();
    const availableDefinitions = useMemo(
        () => getAvailablePipelineItemDefinitions(relationsEnabled),
        [relationsEnabled],
    );

    // const handleWorkspaceMembersSearch = (value: string) => {
    //     try {
    //         authorizedAxiosInstance
    //             .post(`/api/search/database/investments/team/invite`, { query: value })
    //             .then((res: AxiosResponse) => {
    //                 if (res.status === 200) {
    //                     setResults(res.data);
    //                 }
    //             });
    //     } catch (error) {
    //         captureException(error);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleGetResources = useCallback(async () => {
        if (isRelationsPipelineItemCollection(option) && !relationsEnabled) {
            setResults([]);
            setLoading(false);
            return;
        }

        if (user?.id && workspace?.id) {
            try {
                setLoading(true);

                const resources = await workspaceService.getResourceCollection(
                    workspace.id,
                    option as WorkspaceResourceCollection,
                );

                setResults(resources);
                setLoading(false);
            } catch (error) {
                captureException(error);
            } finally {
                setLoading(false);
            }
        }
    }, [option, relationsEnabled, user?.id, workspace?.id]);

    useEffect(() => {
        if (!availableDefinitions.some((definition) => definition.collection === option)) {
            setOption(availableDefinitions[0]?.collection ?? 'investments');
            setInput('');
        }
    }, [availableDefinitions, option]);

    useEffect(() => {
        if (open) {
            handleGetResources();
        }
    }, [handleGetResources, open]);

    const { token } = useAntDesignTheme();
    const selectedDefinition = PIPELINE_ITEM_DEFINITION_BY_COLLECTION[option];
    const filteredResults = results.filter((result) => {
        const value = input.trim().toLowerCase();

        if (!value) {
            return true;
        }

        return [result?.name, result?.title, result?.editedName, result?.tickerSymbol, result?.description]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(value));
    });

    return (
        <Modal
            open={open}
            title={
                <Row align='middle' wrap={false} gutter={[8, 0]}>
                    <Col flex='none'>Add to</Col>
                    <Col flex='none'>
                        <SmallTag
                            bordered
                            icon={
                                <TbCircleFilled
                                    size='0.5rem'
                                    style={{
                                        color: column?.color,
                                        marginRight: '0.375rem',
                                        verticalAlign: '0.05rem',
                                    }}
                                />
                            }
                            message={column?.name}
                            styles={{ marginLeft: 0 }}
                        />
                    </Col>
                </Row>
            }
            onCancel={() => setOpen(false)}
            onOk={() => setOpen(false)}
            width={640}
            footer={
                <Row>
                    <Col flex='none'>
                        <LearnMoreFooter align='left' topic='Pipelines' />
                    </Col>
                    <Col flex='auto' />
                </Row>
            }
            mask={false}
        >
            <div
                style={{
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '32rem',
                    padding: '1rem',
                }}
            >
                <div style={{ color: token.colorTextTertiary }}>
                    <small>Workspace Resource</small>
                </div>
                <Dropdown
                    trigger={['click']}
                    popupRender={() => (
                        <DropdownMenuPanel
                            selectedKeys={[option]}
                            onClick={(e: any) => {
                                setOption(e.key as PipelineItemResourceCollection);
                                setInput('');
                            }}
                            style={{ minWidth: 300 }}
                        >
                            <small
                                style={{
                                    color: token.colorTextTertiary,
                                    display: 'block',
                                    padding: '0.5rem 0.75rem 0.25rem',
                                }}
                            >
                                Workspace Resources
                            </small>
                            {availableDefinitions.map((definition) => (
                                <Menu.Item
                                    key={definition.collection}
                                    style={{
                                        height: 'auto',
                                        lineHeight: 'normal',
                                        padding: '0.5rem 0.75rem',
                                    }}
                                >
                                    <DropdownOptionContent
                                        avatar={{
                                            icon: definition.icon(),
                                            props: { size: 20 },
                                        }}
                                        description={definition.description}
                                        title={definition.pluralLabel}
                                    />
                                </Menu.Item>
                            ))}
                        </DropdownMenuPanel>
                    )}
                >
                    <DropdownButton block style={{ justifyContent: 'flex-start', marginTop: '0.5rem' }}>
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                gap: '0.5rem',
                                minWidth: 0,
                                textAlign: 'left',
                            }}
                        >
                            {selectedDefinition?.icon()}
                            <span>{selectedDefinition?.pluralLabel}</span>
                        </span>
                    </DropdownButton>
                </Dropdown>
                <Input
                    allowClear
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    prefix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                    placeholder={`Filter ${selectedDefinition?.pluralLabel ?? 'Resources'}...`}
                    style={{ marginTop: '0.75rem' }}
                />
                <div style={{ flex: 1, marginTop: '1rem', minHeight: 0 }}>
                    {filteredResults?.length !== 0 ? (
                        <List
                            style={{
                                height: '100%',
                                maxHeight: '26rem',
                                overflow: 'auto',
                            }}
                            dataSource={filteredResults}
                            renderItem={(result: any) => (
                                <ListItem
                                    pipeline={pipeline}
                                    column={column}
                                    result={result}
                                    resource={option}
                                    columns={columns}
                                    setColumns={setColumns}
                                />
                            )}
                        />
                    ) : loading ? (
                        <SkeletonList listLength={6} minHeight='26rem' />
                    ) : (
                        <EmptySection
                            height='26rem'
                            icon={<TbTimelineEventPlus size='75%' style={{ color: token.colorTextTertiary }} />}
                            label={`No ${selectedDefinition?.pluralLabel ?? 'Resources'} to Add`}
                            description='Search for workspace resources to add them to this pipeline stage.'
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AddToColumnModal;
